import React from "react";
import {
  Activity,
  TrendingUp,
  AlertTriangle,
  Check,
  Clock,
} from "lucide-react";
import { useApi } from "@/hooks";
import { analyticsAPI } from "@/api";
import { useDateRange } from "@/contexts/DateRangeContext";
import { cn } from "@/lib/utils";
import { formatDateForAPI } from "@/lib/date-utils";

export function GlobalStatsBar() {
  const { dateRange, selectedLocations } = useDateRange();

  // Fetch dashboard KPIs
  const { data: dashboardData, isLoading: kpisLoading } = useApi(
    () =>
      analyticsAPI.getDashboardKPIs({
        start_date: formatDateForAPI(dateRange.from),
        end_date: formatDateForAPI(dateRange.to),
        ...(selectedLocations.length > 0 && {
          location: selectedLocations.join(","),
        }),
      }),
    { immediate: true, returnFullResponse: true },
  );

  const isLoading = kpisLoading;

  // Extract KPI values safely - handle both response structures
  const getKPIValue = (label: string): number => {
    const kpis = dashboardData?.data?.kpis || dashboardData?.kpis || [];
    const kpi = kpis.find(
      (k: { label: string; value: number }) => k.label === label,
    );
    return kpi?.value || 0;
  };

  const totalReads = getKPIValue("Total Reads");
  const totalSessions = getKPIValue("Total Sessions");
  const matchRate = getKPIValue("Match Rate");
  const exactMatches = getKPIValue("Exact Matches");
  const fuzzyMatches = getKPIValue("Fuzzy Matches");
  const orphanedRecords = getKPIValue("Orphaned Records");
  const activeSessionsCount = getKPIValue("Active Sessions");

  // Calculate percentages
  const totalMatches = totalSessions || 1; // Avoid division by zero
  const fuzzyPercent = (fuzzyMatches / totalMatches) * 100;
  const exactPercent = (exactMatches / totalMatches) * 100;

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />
                <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      icon: Activity,
      label: "Total Sessions",
      value: totalSessions.toLocaleString(),
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: TrendingUp,
      label: "Match Rate",
      value: `${matchRate.toFixed(1)}%`,
      color:
        matchRate >= 90
          ? "text-green-600"
          : matchRate >= 80
            ? "text-yellow-600"
            : "text-red-600",
      bgColor:
        matchRate >= 90
          ? "bg-green-50"
          : matchRate >= 80
            ? "bg-yellow-50"
            : "bg-red-50",
    },
    {
      icon: Check,
      label: "Exact",
      value: exactMatches > 0 ? `${exactPercent.toFixed(1)}%` : "0%",
      subvalue: exactMatches > 0 ? exactMatches.toLocaleString() : "0",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: AlertTriangle,
      label: "Fuzzy",
      value: fuzzyMatches > 0 ? `${fuzzyPercent.toFixed(1)}%` : "0%",
      subvalue: fuzzyMatches > 0 ? fuzzyMatches.toLocaleString() : "0",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      icon: AlertTriangle,
      label: "Orphaned",
      value: orphanedRecords.toLocaleString(),
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
  ];

  return (
    <div className="bg-muted/30 border-b px-6 py-3">
      <div className="flex items-center">
        <div className="flex items-center gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="flex items-center gap-2">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
                  {stat.label}
                </div>
                <div className={cn("text-xl font-bold", stat.color)}>
                  {stat.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
